import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import ProductStatusBadge from "../../../components/marketplace/ProductStatusBadge";
import {
  MarketplaceEmptyState,
  MarketplaceLoadingState,
} from "../../../components/marketplace/MarketplaceStates";
import { listMarketplaceProducts } from "../../../features/marketplace/marketplaceProductApi";
import {
  CATEGORY_LABELS,
  MODERATION_STATUSES,
  PRODUCT_STATUS_LABELS,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import styles from "./SellerMarketplaceDashboard.module.scss";

const TABS = [
  { key: MODERATION_STATUSES.DRAFT, label: "Draft" },
  { key: MODERATION_STATUSES.PENDING, label: "Pending Approval" },
  { key: MODERATION_STATUSES.APPROVED, label: "Approved" },
  { key: MODERATION_STATUSES.REJECTED, label: "Rejected" },
];

const getModerationStatus = (product) =>
  product?.moderationStatus || MODERATION_STATUSES.DRAFT;

export default function SellerMarketplaceDashboard() {
  const { clubId } = useParams();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(MODERATION_STATUSES.DRAFT);
  const [activeImageByProduct, setActiveImageByProduct] = useState({});

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await listMarketplaceProducts(clubId, {
          scope: "mine",
          limit: 100,
        });

        if (!mounted) return;
        setProducts(response?.products || []);
      } catch (err) {
        if (!mounted) return;

        setError(
          err?.response?.data?.message ||
            "Could not load your marketplace listings.",
        );
        setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [clubId]);

  const counts = useMemo(() => {
    const output = {
      [MODERATION_STATUSES.DRAFT]: 0,
      [MODERATION_STATUSES.PENDING]: 0,
      [MODERATION_STATUSES.APPROVED]: 0,
      [MODERATION_STATUSES.REJECTED]: 0,
    };

    products.forEach((product) => {
      const status = getModerationStatus(product);
      if (output[status] !== undefined) output[status] += 1;
    });

    return output;
  }, [products]);

  const visibleProducts = useMemo(
    () =>
      products.filter((product) => getModerationStatus(product) === activeTab),
    [activeTab, products],
  );

  const goToImage = (productId, imageCount, direction) => {
    if (!productId || imageCount <= 1) return;

    setActiveImageByProduct((prev) => {
      const current = Number(prev[productId] || 0);
      const next = (current + direction + imageCount) % imageCount;
      return { ...prev, [productId]: next };
    });
  };

  if (loading) {
    return (
      <MarketplaceLoadingState message="Loading your seller dashboard..." />
    );
  }

  return (
    <section className={styles.page}>
      <div className={styles.bgGlowPink} />
      <div className={styles.bgGlowBlue} />
      <div className={styles.bgGrid} />
      <div className={styles.bgOrbOne} />
      <div className={styles.bgOrbTwo} />

      <header className={styles.hero}>
        <div className={styles.heroLeft}>
          <p className={styles.kicker}>Seller Console</p>
          <h2 className={styles.heroTitle}>Marketplace Products</h2>
          <p className={styles.heroText}>
            Manage your product drafts, approvals, and live listings with a
            clean futuristic dashboard.
          </p>
        </div>

        <div className={styles.heroRight}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Total Listings</span>
            <strong className={styles.statValue}>{products.length}</strong>
          </div>

          <Link className={styles.primaryBtn} to="new">
            <span className={styles.primaryBtnShine} />
            <span className={styles.primaryBtnText}>+ Create Product</span>
          </Link>
        </div>
      </header>

      {error ? (
        <div className={styles.errorCard}>
          <span className={styles.errorDot} />
          <p>{error}</p>
        </div>
      ) : null}

      <section className={styles.tabPanel}>
        <div className={styles.tabPanelHeader}>
          <div>
            <h3>Moderation Queue</h3>
            <p>Switch between statuses to manage your products faster.</p>
          </div>
        </div>

        <nav
          className={styles.tabs}
          aria-label="Seller product moderation tabs"
        >
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${
                activeTab === tab.key ? styles.active : ""
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className={styles.tabGlow} />
              <span className={styles.tabLabel}>{tab.label}</span>
              <span className={styles.tabCount}>{counts[tab.key] || 0}</span>
            </button>
          ))}
        </nav>
      </section>

      {!visibleProducts.length ? (
        <MarketplaceEmptyState
          message={`No ${PRODUCT_STATUS_LABELS[activeTab] || activeTab} products yet.`}
        />
      ) : (
        <ul className={styles.productGrid}>
          {visibleProducts.map((product) => {
            const images =
              product.listingImageUrls || product.listingImageKeys || [];
            const activeIndex = activeImageByProduct[product._id] || 0;
            const activeImage = images[activeIndex];

            return (
              <li key={product._id} className={styles.productCard}>
                <div className={styles.cardGlowPink} />
                <div className={styles.cardGlowBlue} />
                <div className={styles.cardGrid} />

                <div className={styles.mediaWrap}>
                  {images.length ? (
                    <div className={styles.carousel}>
                      <div className={styles.imageFrame}>
                        <img
                          className={styles.carouselImage}
                          src={activeImage}
                          alt={`${product.title} listing`}
                          loading="lazy"
                        />

                        {images.length > 1 ? (
                          <>
                            <button
                              type="button"
                              className={`${styles.carouselBtn} ${styles.prevBtn}`}
                              onClick={() =>
                                goToImage(product._id, images.length, -1)
                              }
                              aria-label="Previous image"
                            >
                              ‹
                            </button>

                            <button
                              type="button"
                              className={`${styles.carouselBtn} ${styles.nextBtn}`}
                              onClick={() =>
                                goToImage(product._id, images.length, 1)
                              }
                              aria-label="Next image"
                            >
                              ›
                            </button>

                            <div className={styles.carouselCount}>
                              <span>{activeIndex + 1}</span>
                              <span className={styles.countDivider}>/</span>
                              <span>{images.length}</span>
                            </div>
                          </>
                        ) : null}
                      </div>

                      {images.length > 1 ? (
                        <div className={styles.dots}>
                          {images.map((_, index) => (
                            <button
                              key={`${product._id}-dot-${index}`}
                              type="button"
                              className={`${styles.dot} ${
                                activeIndex === index ? styles.dotActive : ""
                              }`}
                              onClick={() =>
                                setActiveImageByProduct((prev) => ({
                                  ...prev,
                                  [product._id]: index,
                                }))
                              }
                              aria-label={`Go to image ${index + 1}`}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className={styles.noImage}>No Image</div>
                  )}
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.topRow}>
                    <span className={styles.itemCategory}>
                      {CATEGORY_LABELS[product.category] ||
                        product.category ||
                        "Listing"}
                    </span>

                    <ProductStatusBadge status={getModerationStatus(product)} />
                  </div>

                  <h3 className={styles.itemTitle}>{product.title}</h3>

                  <div className={styles.metaRow}>
                    <span className={styles.metaChip}>
                      {SUBTYPE_LABELS[
                        product.serviceType || product.digitalType
                      ] ||
                        product.serviceType ||
                        product.digitalType ||
                        "General"}
                    </span>

                    <span className={styles.pricePill}>
                      {Number(product.priceNoty || 0)} Noty
                    </span>
                  </div>

                  <div className={styles.cardActions}>
                    <Link to={`${product._id}/edit`} className={styles.editBtn}>
                      Edit Product
                    </Link>

                    <button type="button" className={styles.secondaryBtn}>
                      Preview
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
